import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';

// ─── Request (mirrors server migration.validators.ts) ────────────────────────
export type MigrationCell = string | number | boolean | null;

export interface MigrationSheetRow {
  sheetRow: number;
  cells: Record<string, MigrationCell>;
}

export type MigrationAumUnit = 'crores' | 'rupees';

export interface MigrationSheets {
  rms: MigrationSheetRow[] | null;
  clients: MigrationSheetRow[] | null;
  orders: MigrationSheetRow[] | null;
  additionalArns: MigrationSheetRow[] | null;
  subscriptions: MigrationSheetRow[] | null;
}

export interface MigrationPayload {
  aumUnit: MigrationAumUnit;
  sheets: MigrationSheets;
}

// ─── Report (mirrors server migration.service.ts) ────────────────────────────
export type MigrationSheetName =
  | 'RMs'
  | 'Clients'
  | 'Orders'
  | 'AdditionalARNs'
  | 'LiveSubscriptions';

export interface MigrationIssue {
  level: 'error' | 'warning';
  sheet: MigrationSheetName;
  sheetRow: number | null;
  message: string;
}

export type MigrationRmOutcome = 'existing' | 'create' | 'rejected';
export type MigrationClientOutcome = 'import' | 'existing' | 'skip';

export interface MigrationRmResult {
  rmCode: string;
  name: string;
  jobRole: string;
  sheetRows: number[];
  outcome: MigrationRmOutcome;
  issues: MigrationIssue[];
}

export interface MigrationSubscriptionPreview {
  sheetRow: number;
  productCode: string;
  subItemCode: string;
  nextDueDate: string | null;
  nextDueDateComputed: boolean;
  cycleNumber: number | null;
  annualAmount: number | null;
  perCycleAmount: number | null;
  amountBasis: string | null;
}

export interface MigrationClientResult {
  clientCode: string;
  clientName: string;
  arn: string;
  rmCode: string;
  sheetRows: number[];
  outcome: MigrationClientOutcome;
  crmClientCode: number | null;
  issues: MigrationIssue[];
  subscriptions: MigrationSubscriptionPreview[];
  normalised: {
    email: string | null;
    secondaryEmail: string | null;
    arnAumCrores: number | null;
    additionalArnAum: Record<string, number | null>;
  };
}

export interface MigrationReport {
  mode: 'validate' | 'commit';
  fileErrors: string[];
  summary: {
    rmsExisting: number;
    rmsNew: number;
    rmsRejected: number;
    clientsImport: number;
    clientsExisting: number;
    clientsSkipped: number;
  };
  rms: MigrationRmResult[];
  clients: MigrationClientResult[];
  unlinkedIssues: MigrationIssue[];
}

const post = async (path: string, payload: MigrationPayload): Promise<MigrationReport> => {
  // A full workbook is validated against the database client by client, so it
  // can take well past the default request timeout.
  const { data } = await apiClient.post<ApiResponse<MigrationReport>>(path, payload, {
    timeout: 10 * 60 * 1000,
  });
  if (!data.data) throw new Error('Migration report not found in response');
  return data.data;
};

export const migrationService = {
  /** Dry run: validates the workbook against itself and the CRM, writes nothing. */
  validate: (payload: MigrationPayload): Promise<MigrationReport> =>
    post('/admin/migration/validate', payload),

  /** Re-validates server-side, then writes every RM and client that passes. */
  commit: (payload: MigrationPayload): Promise<MigrationReport> =>
    post('/admin/migration/commit', payload),
};
